import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, CreditCard, Receipt, Activity, MessageSquare, Settings as SettingsIcon,
  LogOut, Search, Bell, Trash2, Eye, Check, X, Clock, Plus, Send, Image as ImageIcon, KeyRound,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip as RTooltip,
} from "recharts";
import api, { GHS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { Logo } from "@/components/Logo";
import { StatusBadge, PredictionPill } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TABS = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "subscriptions", label: "Subscriptions", icon: CreditCard },
  { id: "payments", label: "Payments", icon: Receipt },
  { id: "predictions", label: "Predictions", icon: Activity },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [pendingPayments, setPendingPayments] = useState(0);

  const refreshAlerts = useCallback(() => {
    api.get("/admin/stats").then(({ data }) => setPendingPayments(data.pending_payments)).catch(() => {});
  }, []);
  useEffect(() => { refreshAlerts(); }, [refreshAlerts, tab]);

  return (
    <div className="min-h-screen bg-[#0B0F17]">
      <header className="glass sticky top-0 z-40">
        <div className="vw-container flex items-center justify-between h-16">
          <div className="flex items-center gap-3"><Logo /><span className="hidden sm:inline text-xs font-mono uppercase tracking-widest text-emerald-400 border border-emerald-500/30 rounded-full px-2 py-0.5">Admin</span></div>
          <div className="flex items-center gap-4">
            {pendingPayments > 0 && (
              <button onClick={() => setTab("payments")} className="relative text-slate-300 hover:text-white" data-testid="admin-alert-bell">
                <Bell size={20} />
                <span className="absolute -top-1.5 -right-1.5 h-4 min-w-4 px-1 grid place-items-center rounded-full bg-emerald-500 text-[10px] font-bold text-black">{pendingPayments}</span>
              </button>
            )}
            <button onClick={() => { logout(); navigate("/"); }} className="text-sm text-slate-400 hover:text-red-400 inline-flex items-center gap-1.5" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      {pendingPayments > 0 && (
        <div className="vw-container pt-4">
          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300 flex items-center gap-2" data-testid="admin-payment-alert">
            <Bell size={16} /> {pendingPayments} new payment{pendingPayments > 1 ? "s" : ""} awaiting verification.
          </div>
        </div>
      )}

      <div className="vw-container py-5">
        <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`whitespace-nowrap inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                tab === t.id ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "text-slate-400 border-transparent hover:text-white hover:bg-slate-800/60"
              }`} data-testid={`admin-tab-${t.id}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          {tab === "overview" && <Overview />}
          {tab === "users" && <UsersTab />}
          {tab === "subscriptions" && <SubscriptionsTab />}
          {tab === "payments" && <PaymentsTab onChange={refreshAlerts} />}
          {tab === "predictions" && <PredictionsTab />}
          {tab === "messages" && <MessagesTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- OVERVIEW ---------------- */
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)); }, []);
  if (!stats) return <Loading />;
  const cards = [
    { label: "Total Users", value: stats.total_users, color: "#00FF66" },
    { label: "Active Users", value: stats.active_users, color: "#10B981" },
    { label: "Pending Users", value: stats.pending_users, color: "#F59E0B" },
    { label: "Suspended Users", value: stats.suspended_users, color: "#F87171" },
    { label: "Active Subscriptions", value: stats.active_subscriptions, color: "#00FF66" },
    { label: "Expired Subscriptions", value: stats.expired_subscriptions, color: "#94A3B8" },
    { label: "Total Analyses", value: stats.total_analyses, color: "#3B82F6" },
    { label: "Pending Payments", value: stats.pending_payments, color: "#F59E0B" },
  ];
  const chartData = [
    { name: "Active", value: stats.active_users, color: "#10B981" },
    { name: "Pending", value: stats.pending_users, color: "#F59E0B" },
    { name: "Suspended", value: stats.suspended_users, color: "#F87171" },
  ];
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="vw-card p-5" data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="mt-2 font-display text-3xl font-black" style={{ color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>
      <div className="vw-card p-6">
        <h3 className="font-display font-bold text-white mb-4">User Distribution</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
              <RTooltip contentStyle={{ background: "#161E2E", border: "1px solid #1E293B", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {chartData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/* ---------------- USERS ---------------- */
function UsersTab() {
  const [users, setUsers] = useState(null);
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [resets, setResets] = useState([]);

  const load = useCallback((q = "") => api.get(`/admin/users`, { params: { search: q } }).then(({ data }) => setUsers(data)), []);
  const loadResets = useCallback(() => api.get("/admin/reset-requests").then(({ data }) => setResets(data.filter((r) => r.status === "PENDING"))), []);
  useEffect(() => { load(); loadResets(); }, [load, loadResets]);

  const setStatus = async (id, status) => {
    await api.post(`/admin/users/${id}/status`, null, { params: { status } });
    toast.success(`User set to ${status}`); load(search);
  };
  const del = async (id) => {
    if (!window.confirm("Delete this user and all their data?")) return;
    await api.delete(`/admin/users/${id}`); toast.success("User deleted"); load(search);
  };
  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/users/${id}`); setDetail(data);
  };
  const resetPw = async (id, phone) => {
    const pw = window.prompt(`Set a new password for ${phone}:`);
    if (!pw) return;
    if (pw.length < 4) { toast.error("Password must be at least 4 characters."); return; }
    await api.post(`/admin/users/${id}/reset-password`, { new_password: pw });
    toast.success("Password reset. Share it with the user."); loadResets();
  };

  const resetPhones = new Set(resets.map((r) => r.phone));

  return (
    <div data-testid="admin-users">
      {resets.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-300 mb-4 flex items-center gap-2" data-testid="admin-reset-alert">
          <KeyRound size={16} /> {resets.length} password reset request{resets.length > 1 ? "s" : ""} pending: {resets.map((r) => r.phone).join(", ")}
        </div>
      )}
      <div className="relative mb-4 max-w-md">
        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input value={search} onChange={(e) => { setSearch(e.target.value); load(e.target.value); }} placeholder="Search by phone…" className="vw-input !pl-11" data-testid="admin-user-search" />
      </div>
      {users === null ? <Loading /> : users.length === 0 ? <Empty text="No users found." /> : (
        <div className="space-y-3">
          {users.map((u) => (
            <div key={u.id} className="vw-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-user-${u.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-white flex items-center gap-2">
                  {u.phone}
                  {resetPhones.has(u.phone) && <span className="text-[10px] font-mono text-amber-400 border border-amber-500/30 rounded px-1.5 py-0.5">RESET REQUESTED</span>}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{u.subscription?.has_subscription ? `${u.subscription.plan_name} · ${u.subscription.status}` : "No subscription"}</div>
              </div>
              <StatusBadge status={u.status} />
              <div className="flex items-center gap-1.5 flex-wrap">
                <ActionBtn onClick={() => openDetail(u.id)} icon={Eye} label="View" testid={`view-user-${u.id}`} />
                {u.status !== "ACTIVE" && <ActionBtn onClick={() => setStatus(u.id, "ACTIVE")} icon={Check} label="Activate" color="#00FF66" testid={`activate-user-${u.id}`} />}
                {u.status !== "SUSPENDED" && <ActionBtn onClick={() => setStatus(u.id, "SUSPENDED")} icon={X} label="Suspend" color="#F59E0B" testid={`suspend-user-${u.id}`} />}
                <ActionBtn onClick={() => resetPw(u.id, u.phone)} icon={KeyRound} label="Reset PW" color="#60A5FA" testid={`reset-user-${u.id}`} />
                <ActionBtn onClick={() => del(u.id)} icon={Trash2} label="Delete" color="#F87171" testid={`delete-user-${u.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#161E2E] border-slate-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display">User Details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm" data-testid="admin-user-detail">
              <KV k="Phone number" v={detail.user.phone} />
              <KV k="Account status" v={<StatusBadge status={detail.user.status} />} />
              <KV k="Current plan" v={detail.subscription?.has_subscription ? detail.subscription.plan_name : "—"} />
              <KV k="Subscription status" v={<StatusBadge status={detail.subscription?.status || "NONE"} />} />
              <KV k="Amount paid" v={GHS(detail.amount_paid)} />
              <KV k="Activation date" v={detail.subscription?.activated_at ? new Date(detail.subscription.activated_at).toLocaleString() : "—"} />
              <KV k="Expiry date" v={detail.subscription?.expires_at ? new Date(detail.subscription.expires_at).toLocaleString() : "—"} />
              <KV k="Total analyses" v={detail.total_analyses} />
              <KV k="Analyses used" v={detail.subscription?.type === "shot" ? (detail.subscription.credits_total - detail.subscription.credits_remaining) : "—"} />
              <KV k="Remaining analyses" v={detail.subscription?.has_subscription ? (detail.subscription.type === "time_pass" ? "UNLIMITED" : detail.subscription.credits_remaining) : "—"} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- SUBSCRIPTIONS ---------------- */
function SubscriptionsTab() {
  const [subs, setSubs] = useState(null);
  const load = useCallback(() => api.get("/admin/subscriptions").then(({ data }) => setSubs(data)), []);
  useEffect(() => { load(); }, [load]);

  const extend = async (id) => {
    const hours = Number(window.prompt("Add hours (0 to skip):", "24") || 0);
    const credits = Number(window.prompt("Add credits (0 to skip):", "0") || 0);
    await api.post(`/admin/subscriptions/${id}/extend`, { hours, credits });
    toast.success("Subscription extended"); load();
  };
  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this subscription?")) return;
    await api.post(`/admin/subscriptions/${id}/deactivate`); toast.success("Deactivated"); load();
  };

  return (
    <div data-testid="admin-subscriptions">
      {subs === null ? <Loading /> : subs.length === 0 ? <Empty text="No subscriptions yet." /> : (
        <div className="space-y-3">
          {subs.map((s) => (
            <div key={s.id} className="vw-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`admin-sub-${s.id}`}>
              <div>
                <div className="font-semibold text-white">{s.user_phone} · {s.plan_name}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {s.type === "time_pass" ? `Expires ${s.expires_at ? new Date(s.expires_at).toLocaleString() : "—"}` : `${s.credits_remaining}/${s.credits_total} credits`}
                </div>
              </div>
              <StatusBadge status={s.status} />
              <div className="flex items-center gap-1.5">
                <ActionBtn onClick={() => extend(s.id)} icon={Clock} label="Extend" color="#00FF66" testid={`extend-sub-${s.id}`} />
                <ActionBtn onClick={() => deactivate(s.id)} icon={X} label="Deactivate" color="#F87171" testid={`deactivate-sub-${s.id}`} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- PAYMENTS ---------------- */
function PaymentsTab({ onChange }) {
  const [payments, setPayments] = useState(null);
  const [filter, setFilter] = useState("PENDING");
  const [proof, setProof] = useState(null);

  const load = useCallback((f) => api.get("/admin/payments", { params: f ? { status: f } : {} }).then(({ data }) => setPayments(data)), []);
  useEffect(() => { load(filter); }, [load, filter]);

  const decide = async (id, action) => {
    await api.post(`/admin/payments/${id}/${action}`);
    toast.success(`Payment ${action}d`); load(filter); onChange?.();
  };

  return (
    <div data-testid="admin-payments">
      <div className="flex gap-2 mb-4">
        {["PENDING", "APPROVED", "REJECTED", ""].map((f) => (
          <button key={f || "ALL"} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${filter === f ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "text-slate-400 border-slate-800"}`}
            data-testid={`pay-filter-${f || "all"}`}>{f || "ALL"}</button>
        ))}
      </div>
      {payments === null ? <Loading /> : payments.length === 0 ? <Empty text="No payments here." /> : (
        <div className="space-y-3">
          {payments.map((p) => (
            <div key={p.id} className="vw-card p-4" data-testid={`admin-payment-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-white">{p.user_phone} · {p.plan_name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{new Date(p.submitted_at).toLocaleString()} {p.reference ? `· Ref ${p.reference}` : ""}</div>
                </div>
                <div className="font-display font-bold text-white">{GHS(p.amount)}</div>
                <StatusBadge status={p.status} />
                <div className="flex items-center gap-1.5">
                  <ActionBtn onClick={() => setProof(p)} icon={ImageIcon} label="Proof" testid={`view-proof-${p.id}`} />
                  {p.status === "PENDING" && <>
                    <ActionBtn onClick={() => decide(p.id, "approve")} icon={Check} label="Approve" color="#00FF66" testid={`approve-${p.id}`} />
                    <ActionBtn onClick={() => decide(p.id, "reject")} icon={X} label="Reject" color="#F87171" testid={`reject-${p.id}`} />
                  </>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!proof} onOpenChange={(o) => !o && setProof(null)}>
        <DialogContent className="bg-[#161E2E] border-slate-800 text-white max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Payment Proof — {proof?.plan_name}</DialogTitle></DialogHeader>
          {proof && (
            <div data-testid="admin-proof-modal">
              <img src={proof.proof_base64} alt="proof" className="w-full rounded-xl border border-slate-800 max-h-[60vh] object-contain bg-black/40" />
              <div className="mt-3 text-sm text-slate-400">{proof.user_phone} · {GHS(proof.amount)} · {new Date(proof.submitted_at).toLocaleString()}</div>
              {proof.status === "PENDING" && (
                <div className="flex gap-2 mt-4">
                  <button onClick={() => decide(proof.id, "approve").then(() => setProof(null))} className="vw-btn-primary flex-1 !py-2.5" data-testid="modal-approve">Approve</button>
                  <button onClick={() => decide(proof.id, "reject").then(() => setProof(null))} className="vw-btn-outline flex-1 !py-2.5 hover:!text-red-400 hover:!border-red-500/50" data-testid="modal-reject">Reject</button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- PREDICTIONS ---------------- */
function PredictionsTab() {
  const [preds, setPreds] = useState(null);
  const [img, setImg] = useState(null);
  useEffect(() => { api.get("/admin/predictions").then(({ data }) => setPreds(data)); }, []);
  return (
    <div data-testid="admin-predictions">
      {preds === null ? <Loading /> : preds.length === 0 ? <Empty text="No analyses performed yet." /> : (
        <div className="space-y-3">
          {preds.map((p) => (
            <div key={p.id} className="vw-card p-4" data-testid={`admin-pred-${p.id}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <span className="text-sm text-white font-medium">{p.user_phone}</span>
                <span className="text-xs text-slate-500 font-mono">{new Date(p.created_at).toLocaleString()} · {p.plan_used}</span>
              </div>
              <div className="flex gap-4 flex-col sm:flex-row">
                {p.screenshot_base64 && <img src={p.screenshot_base64} alt="" onClick={() => setImg(p.screenshot_base64)} className="w-full sm:w-28 h-20 object-cover rounded-lg border border-slate-800 cursor-pointer" />}
                <div className="flex-1 space-y-1.5">
                  {p.fixtures.map((f, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 text-sm">
                      <span className="text-slate-200">{f.home_team} <span className="text-slate-500">vs</span> {f.away_team}</span>
                      <PredictionPill prediction={f.prediction} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!img} onOpenChange={(o) => !o && setImg(null)}>
        <DialogContent className="bg-[#161E2E] border-slate-800 max-w-2xl">
          {img && <img src={img} alt="screenshot" className="w-full rounded-xl max-h-[75vh] object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- MESSAGES ---------------- */
function MessagesTab() {
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState("all");
  const [selected, setSelected] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/admin/users").then(({ data }) => setUsers(data)); }, []);

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const send = async () => {
    if (!title || !content) { toast.error("Title and content required."); return; }
    if (target === "selected" && selected.length === 0) { toast.error("Select at least one user."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/messages/broadcast", { target, user_ids: selected, title, content });
      toast.success(`Message sent to ${data.sent} user(s)`); setTitle(""); setContent(""); setSelected([]);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to send."); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-messages">
      <div className="vw-card p-6 space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Recipients</label>
          <div className="flex gap-2">
            <button onClick={() => setTarget("all")} className={`px-4 py-2 rounded-xl text-sm border ${target === "all" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "text-slate-400 border-slate-800"}`} data-testid="msg-target-all">All Users</button>
            <button onClick={() => setTarget("selected")} className={`px-4 py-2 rounded-xl text-sm border ${target === "selected" ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30" : "text-slate-400 border-slate-800"}`} data-testid="msg-target-selected">Selected Users</button>
          </div>
        </div>
        {target === "selected" && (
          <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-800 p-2 space-y-1" data-testid="msg-user-list">
            {users.map((u) => (
              <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-800/60 cursor-pointer text-sm">
                <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`msg-user-${u.id}`} />
                <span className="text-slate-200">{u.phone}</span>
              </label>
            ))}
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">Title</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className="vw-input" placeholder="Message title" data-testid="msg-title" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">Content</label>
          <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={4} className="vw-input resize-none" placeholder="Write your message…" data-testid="msg-content" />
        </div>
        <button onClick={send} disabled={loading} className="vw-btn-primary w-full !py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="msg-send">
          <Send size={18} /> {loading ? "Sending…" : "Send Message"}
        </button>
      </div>
    </div>
  );
}

/* ---------------- SETTINGS ---------------- */
function SettingsTab() {
  const { setSettings } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then(({ data }) => setForm(data)); }, []);
  if (!form) return <Loading />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPlan = (i, k, v) => setForm((f) => { const plans = [...f.plans]; plans[i] = { ...plans[i], [k]: v }; return { ...f, plans }; });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", form);
      setForm(data);
      // refresh public settings
      const pub = await api.get("/settings/public"); setSettings(pub.data);
      toast.success("Settings saved");
    } catch (err) { toast.error("Save failed."); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-settings">
      <div className="vw-card p-6 space-y-4">
        <h3 className="font-display font-bold text-white">Payment & Support</h3>
        <TextField label="Payment number" value={form.payment_number} onChange={(v) => set("payment_number", v)} testid="set-pay-number" />
        <TextField label="Recipient name" value={form.payment_name} onChange={(v) => set("payment_name", v)} testid="set-pay-name" />
        <TextAreaField label="Payment instructions" value={form.payment_instructions} onChange={(v) => set("payment_instructions", v)} testid="set-pay-instructions" />
        <TextAreaField label="Support information" value={form.support_info} onChange={(v) => set("support_info", v)} testid="set-support-info" />
      </div>

      <div className="vw-card p-6 space-y-4">
        <h3 className="font-display font-bold text-white">Announcement Bar</h3>
        <TextField label="Announcement text" value={form.announcement_text} onChange={(v) => set("announcement_text", v)} testid="set-announce-text" />
        <label className="flex items-center gap-2 text-sm text-slate-300">
          <input type="checkbox" checked={form.announcement_enabled} onChange={(e) => set("announcement_enabled", e.target.checked)} data-testid="set-announce-enabled" />
          Enable announcement bar
        </label>
      </div>

      <div className="vw-card p-6 space-y-4">
        <h3 className="font-display font-bold text-white">Plans</h3>
        {form.plans.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-800 p-4" data-testid={`set-plan-${i}`}>
            <div className="font-semibold text-white mb-3">{p.name}</div>
            <div className="grid grid-cols-2 gap-3">
              <MiniField label="Price (GHS)" type="number" value={p.price} onChange={(v) => setPlan(i, "price", Number(v))} testid={`set-plan-price-${i}`} />
              {p.type === "shot"
                ? <MiniField label="Credits" type="number" value={p.credits} onChange={(v) => setPlan(i, "credits", Number(v))} testid={`set-plan-credits-${i}`} />
                : <MiniField label="Duration (hours)" type="number" value={p.duration_hours} onChange={(v) => setPlan(i, "duration_hours", Number(v))} testid={`set-plan-hours-${i}`} />}
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 mt-3">
              <input type="checkbox" checked={p.available !== false} onChange={(e) => setPlan(i, "available", e.target.checked)} data-testid={`set-plan-available-${i}`} />
              Available
            </label>
          </div>
        ))}
      </div>

      <button onClick={save} disabled={saving} className="vw-btn-primary !py-3 px-8 inline-flex items-center gap-2 disabled:opacity-60" data-testid="settings-save">
        {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
}

/* ---------------- shared bits ---------------- */
const Loading = () => <div className="h-40 grid place-items-center"><div className="h-9 w-9 rounded-full border-2 border-emerald-500/30 border-t-emerald-400 animate-spin" /></div>;
const Empty = ({ text }) => <div className="vw-card p-10 text-center text-slate-400">{text}</div>;
const KV = ({ k, v }) => <div className="flex items-center justify-between gap-4 border-b border-slate-800 pb-2"><span className="text-slate-400">{k}</span><span className="text-white font-medium">{v}</span></div>;
const ActionBtn = ({ onClick, icon: Icon, label, color = "#94A3B8", testid }) => (
  <button onClick={onClick} title={label} className="h-9 px-2.5 rounded-lg border border-slate-800 hover:border-slate-600 inline-flex items-center gap-1 text-xs" style={{ color }} data-testid={testid}>
    <Icon size={15} /> <span className="hidden sm:inline">{label}</span>
  </button>
);
const TextField = ({ label, value, onChange, testid }) => (
  <div><label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label><input value={value || ""} onChange={(e) => onChange(e.target.value)} className="vw-input" data-testid={testid} /></div>
);
const TextAreaField = ({ label, value, onChange, testid }) => (
  <div><label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label><textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="vw-input resize-none" data-testid={testid} /></div>
);
const MiniField = ({ label, value, onChange, type = "text", testid }) => (
  <div><label className="block text-xs font-medium text-slate-400 mb-1">{label}</label><input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="vw-input !py-2" data-testid={testid} /></div>
);
